/**
 * `messages` procedure group — the email lane's read surface (Wave-2 track D2).
 *
 * What is on this contract is decided entirely by design §1.8's retention rule:
 * only threads with a CRM contact on them exist at all, so there is no "list my
 * mailbox" here and there never will be. Everything is anchored to a contact or
 * to a thread we already decided to keep.
 *
 * ## Why the body is its own procedure
 *
 * `messages.thread.messages` returns metadata plus a snippet; the body comes
 * from `messages.body`, one message at a time. Three reasons, in order of how
 * much they matter:
 *
 *  1. **Scope projection.** A thread page for a SHARED contact must not carry
 *     bodies. Keeping the body behind its own call means the read that could
 *     leak it is a single, separately-authorised procedure rather than a field
 *     buried in a list response.
 *  2. **Size.** A forty-message thread with quoted chains is megabytes. A list
 *     endpoint that returned all of it would time out on the one thread a user
 *     most wants to open.
 *  3. **Storage.** The body is in S3, not Postgres. One blob fetch per opened
 *     message is the honest cost; forty per page is not.
 *
 * The body is STREAMED THROUGH THE API rather than handed out as a presigned
 * URL. Design §3 allows either; this track chose the former because a presigned
 * URL is a bearer credential for a private email body with no tenant check
 * attached, and it is far easier to add one later than to recall one.
 */

import { z } from 'zod';
import {
  cursorSchema,
  isoDateTimeSchema,
  jsonObjectSchema,
  paginationInputSchema,
  uuidSchema,
} from './common.ts';

/**
 * Who holds the next action on a thread — `email.thread_summary`'s output, and
 * the driver of the digest's "awaiting-me threads older than 2 days" signal.
 */
export const THREAD_STATES = ['awaiting_me', 'awaiting_them', 'closed'] as const;
export type ThreadState = (typeof THREAD_STATES)[number];
export const threadStateSchema = z.enum(THREAD_STATES);

export const MESSAGE_ROLES = ['from', 'to', 'cc', 'bcc'] as const;
export type MessageRole = (typeof MESSAGE_ROLES)[number];
export const messageRoleSchema = z.enum(MESSAGE_ROLES);

export const messageThreadSchema = z.object({
  id: uuidSchema,
  /** `email` today. The column is `channel`, so LinkedIn threads fit later. */
  channel: z.string(),
  subject: z.string().nullable(),
  /** Every address on the thread, normalized. */
  participantEmails: z.array(z.string()),
  /** The CRM contacts on it. Non-empty by construction — see the header. */
  participantContactIds: z.array(uuidSchema),
  messageCount: z.int().min(0),
  firstMessageAt: isoDateTimeSchema.nullable(),
  lastMessageAt: isoDateTimeSchema.nullable(),

  // --- `email.thread_summary` output, null until first summarised ------------
  threadState: threadStateSchema.nullable(),
  /** The timeline preview. <= 200 chars, enforced by the task's guard. */
  oneLine: z.string().nullable(),
  summary: z.string().nullable(),
  sentiment: z.string().nullable(),
  summaryConfidence: z.number().min(0).max(1).nullable(),
  summarizedAt: isoDateTimeSchema.nullable(),
  /**
   * Which model wrote the summary. On the wire because the web marks AI-derived
   * text as such (design §2.5's provenance rule) and "unknown model" and "no
   * summary" must be distinguishable.
   */
  summaryModel: z.string().nullable(),
});

export const messageParticipantSchema = z.object({
  role: messageRoleSchema,
  address: z.string(),
  displayName: z.string().nullable(),
  /** Null when the address resolves to no contact, or to more than one. */
  contactId: uuidSchema.nullable(),
});

export const messageSchema = z.object({
  id: uuidSchema,
  threadId: uuidSchema,
  direction: z.enum(['inbound', 'outbound', 'mutual', 'system']),
  sentAt: isoDateTimeSchema,
  subject: z.string().nullable(),
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
  toAddresses: z.array(z.string()),
  ccAddresses: z.array(z.string()),
  /** <= 500 chars. Safe to render in a list. */
  snippet: z.string().nullable(),
  /**
   * Whether a body is stored. NOT the key: an S3 key is an implementation
   * detail and putting one on the wire invites a client to try to fetch it.
   */
  hasBody: z.boolean(),
  hasAttachments: z.boolean(),
  /** `gmail_api` | `graph` | `extension`. Provenance, for the timeline. */
  capturedVia: z.string().nullable(),
  participants: z.array(messageParticipantSchema),
});

export const messageBodySchema = z.object({
  messageId: uuidSchema,
  threadId: uuidSchema,
  subject: z.string().nullable(),
  sentAt: isoDateTimeSchema,
  /**
   * The body text, streamed through the API. Null when none was stored — a real
   * state (a fetch that failed, a retention delete), rendered as "not stored"
   * rather than as an error.
   */
  body: z.string().nullable(),
  mimeType: z.string().nullable(),
  /** Set when the body was truncated to fit the response. */
  truncated: z.boolean(),
});

// --- inputs ------------------------------------------------------------------

export const messageThreadsListInputSchema = paginationInputSchema.extend({
  /** Narrow to one contact's threads. The common case, from a contact page. */
  contactId: uuidSchema.optional(),
  threadState: threadStateSchema.optional(),
});

export const messageThreadsListOutputSchema = z.object({
  items: z.array(messageThreadSchema),
  nextCursor: cursorSchema.nullable(),
});

export const messageThreadGetInputSchema = z.object({
  threadId: uuidSchema,
});

export const messageThreadMessagesInputSchema = paginationInputSchema.extend({
  threadId: uuidSchema,
});

export const messageThreadMessagesOutputSchema = z.object({
  items: z.array(messageSchema),
  nextCursor: cursorSchema.nullable(),
});

export const messageBodyInputSchema = z.object({
  messageId: uuidSchema,
});

// --- sync toggle -------------------------------------------------------------

/**
 * `sync.accounts.mailToggle`.
 *
 * POST rather than PATCH on the account, because ENABLING has a side effect
 * beyond the flag: it enqueues `mail.watch`, which arms push and starts the
 * bounded backfill. A field on a generic account PATCH would hide a
 * twelve-month history walk behind a checkbox update.
 */
export const syncMailToggleInputSchema = z.object({
  accountId: uuidSchema,
  enabled: z.boolean(),
  /**
   * Whether enabling should also walk the last 12 months. Default true: a user
   * connecting mail expects their history, and the alternative (an empty
   * timeline that fills only with new mail) reads as broken.
   */
  backfill: z.boolean().default(true),
});

export const syncMailToggleOutputSchema = z.object({
  accountId: uuidSchema,
  mailSyncEnabled: z.boolean(),
  /** `pending | running | complete | failed`. Mirrors the account row. */
  mailBackfillState: z.string(),
  /** False when the lane is not configured; the UI says so rather than lying. */
  queued: z.boolean(),
  /** Why nothing was queued, when it was not. */
  reason: z.string().nullable(),
  evidence: jsonObjectSchema,
});

// --- inferred types ----------------------------------------------------------

export type MessageThread = z.output<typeof messageThreadSchema>;
export type Message = z.output<typeof messageSchema>;
export type MessageBody = z.output<typeof messageBodySchema>;
export type MessageThreadsListInput = z.input<typeof messageThreadsListInputSchema>;
export type SyncMailToggleInput = z.input<typeof syncMailToggleInputSchema>;
